import React, { useState, useMemo, useContext, useRef  } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from "react-i18next";
import { BlogService } from '../Blog/services/BlogService';
import { Theme } from '@material-ui/core';
import { 
  Dialog, 
  TextField, 
  Button, 
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography
} from '@material-ui/core';
import { Search, Info, ChevronLeft, ChevronRight } from '@material-ui/icons';
import { BlogContext } from '../../contexts/AppContext';
import i18n from '../../i18n/config';


const useStyles = makeStyles((theme: Theme) => ({
    navbar: {
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: 'rgb(221, 221, 221)',
        height: 'auto',
        padding: '15px 0px 15px 100px',
        borderBottom: '1px solid #ddd',
        width: '100%',
        boxSizing: 'border-box',
        zIndex: 999,
        position: 'relative',
        overflow: 'visible', 
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            padding: '10px 0px 10px 20px',
            position: 'relative',
        },
    },
    navContainer: {
        display: 'flex',
        width: '100%',
        position: 'relative',
        alignItems: 'center',
        overflow: 'visible',
    },
    navItems: {
        display: 'flex',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        scrollBehavior: 'smooth',
        '-webkit-overflow-scrolling': 'touch',
        
        // Remove ALL overflow properties from navItems
        // Let the wrapper handle scrolling
        overflow: 'visible',
        position: 'relative',
        
        [theme.breakpoints.down('sm')]: {
            // No overflow properties here
            paddingBottom: '5px',
            width: 'calc(100% - 80px)',
            // Force horizontal layout without clipping
            flexWrap: 'nowrap',
        },
        [theme.breakpoints.up('md')]: {
            // No overflow needed on desktop
        },
    },

    // Add a new wrapper class for scrolling
    navItemsWrapper: {
        [theme.breakpoints.down('sm')]: {
            overflowX: 'auto',
            overflowY: 'hidden', // Explicitly hidden
            width: 'calc(100% - 80px)',
            // Prevent vertical scrolling but allow dropdowns to extend
            position: 'relative',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        },
        [theme.breakpoints.up('md')]: {
            overflow: 'visible',
        },
    },
    scrollButton: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '50%',
        minWidth: '30px',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    scrollLeft: {
        left: '5px',
    },
    scrollRight: {
        right: '5px',
    },
    navLink: {
        fontSize: '14px',
        color: '#000',
        marginRight: '20px',
        textDecoration: 'none',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontFamily: 'Roboto, sans-serif',
        border: 'none',
        outline: 'none',
        background: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    dropdown: {
        position: 'relative',
        display: 'inline-block',
        // Ensure dropdown container doesn't clip content
        //overflow: 'visible',
    },
    dropdownContent: {
        display: 'none',
        position: 'absolute',
        top: '100%',
        left: '0',
        backgroundColor: '#fff',
        minWidth: '160px',
        zIndex: 9999,
        boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',
        borderRadius: '8px',
        overflow: 'visible',
        border: '1px solid #ddd',
        // Ensure dropdown can extend beyond nav container
        transform: 'translateZ(0)', // Force hardware acceleration
        willChange: 'transform',
        '& a': {
            color: '#000',
            fontWeight: 'bold',
            fontSize: '14px',
            padding: '12px 16px',
            textDecoration: 'none',
            fontFamily: 'Roboto, sans-serif',
            display: 'block',
            borderBottom: '1px solid #ddd',
            '&:last-child': {
                borderBottom: 'none',
            },
            '&:hover': {
                backgroundColor: '#ddd',
            },
        },
    },
    dropdownShow: {
        display: 'block !important',
    },
    searchDialog: {
        '& .MuiDialog-paper': {
            borderRadius: '12px',
            padding: theme.spacing(2),
            minWidth: '400px',
        },
    },
    searchField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    searchButtonDialog: {
        borderRadius: '20px',
        textTransform: 'none',
        fontWeight: 'bold',
        padding: '8px 24px',
        backgroundColor: '#890608',
    },
    examplesContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
    },
    infoIcon: {
        color: "#890608",
        fontSize: '16px',
    },
    searchButton: {
        fontSize: '14px',
        color: '#000',
        marginRight: '20px',
        textDecoration: 'none',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontFamily: 'Roboto, sans-serif',
        border: 'none',
        outline: 'none',
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

const NavbarBlog = () => {
    const classes = useStyles();
    const { t } = useTranslation<'common'>();
    const history = useHistory();
    const [showCategories, setShowCategories] = useState(false);
    const [showPartners, setShowPartners] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const blogService = useMemo(() => new BlogService(), []);
    const navItemsRef = useRef<HTMLDivElement>(null);

    // Use BlogContext
    const { blogData } = useContext(BlogContext);
    const { categories, partners } = blogData;

    // Filter categories and partners based on dropdown flags
    const filteredCategories = useMemo(() => {
        if (!categories || !Array.isArray(categories)) return [];
        return categories.filter(category => category.categoryDropdown === 'true');
    }, [categories]);

    const filteredPartners = useMemo(() => {
        if (!partners || !Array.isArray(partners)) return [];
        return partners.filter(partner => partner.partnerDropdown === "true");
    }, [partners]);

    
    // Scroll functionality
    const scroll = (direction: 'left' | 'right') => {
        if (navItemsRef.current) {
            const scrollAmount = 200;
            const newScrollLeft = navItemsRef.current.scrollLeft + 
                (direction === 'left' ? -scrollAmount : scrollAmount);
            navItemsRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });
        }
    };

    // Open search dialog
    const handleSearchOpen = () => {
        setSearchOpen(true);
    };

    // Close search dialog
    const handleSearchClose = () => {
        setSearchOpen(false);
        setSearchTerm('');
    };

    // Handle search submission
    const handleSearchSubmit = async () => {
        if (searchTerm.trim()) {
            try {
                const searchResults = await blogService.searchArticles(searchTerm.trim());
                history.push(`/blogsearch?q=${encodeURIComponent(searchTerm.trim())}`, {
                    state: { 
                        searchResults,
                        searchTerm: searchTerm.trim()
                    }
                });
                handleSearchClose();
            } catch (error) {
                console.error('Search error:', error);
                history.push(`/blogsearch?q=${encodeURIComponent(searchTerm.trim())}`);
                handleSearchClose();
            }
        }
    };

    // Handle Enter key press in search field
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

  

    // Only render on /blog pages
    if (!location.pathname.startsWith('/blog')) {
        return null;
    }

    return (
        <nav className={classes.navbar}>
            <div className={classes.navContainer}>
                {/* Left scroll button for mobile */}
                <IconButton 
                    className={`${classes.scrollButton} ${classes.scrollLeft}`}
                    onClick={() => scroll('left')}
                    size="small"
                >
                    <ChevronLeft />
                </IconButton>

                {/* New scrolling wrapper */}
                <div className={classes.navItemsWrapper}>
                    <div 
                        className={classes.navItems} 
                        ref={navItemsRef}
                    >
                        <Link to="/blog" className={classes.navLink}>
                            {t("blogNavbar.accueil")}
                        </Link>

                        {/* Categories Dropdown */}
                        {filteredCategories.length > 0 && (
                            <div
                                className={classes.dropdown}
                                onMouseEnter={() => setShowCategories(true)}
                                onMouseLeave={() => setShowCategories(false)}
                            >
                                <button className={classes.navLink}>
                                    {t("blogNavbar.nos-rubriques")}
                                </button>
                                <div className={`${classes.dropdownContent} ${showCategories ? classes.dropdownShow : ''}`}>
                                    {filteredCategories.map((category) => (
                                        <Link to={`/blog/rubrique/${category.name.slug}`} key={category._id}>
                                            {i18n.language === 'fr' ? category.name.fr : category.name.en}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Partners Dropdown */}
                        {filteredPartners.length > 0 && (
                            <div
                                className={classes.dropdown}
                                onMouseEnter={() => setShowPartners(true)}
                                onMouseLeave={() => setShowPartners(false)}
                            >
                                <button className={classes.navLink}>
                                    {t("blogNavbar.partenaires")}
                                </button>
                                <div className={`${classes.dropdownContent} ${showPartners ? classes.dropdownShow : ''}`}>
                                    {filteredPartners.map(partner => (
                                        <Link to={`/blog/partenaires/${partner.slug}`} key={partner._id}>
                                            {partner.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Link to="/blog/rubrique/guides" className={classes.navLink}>
                            {t("blogNavbar.guides")}
                        </Link>

                        <button className={classes.searchButton} onClick={handleSearchOpen}>
                            <Search fontSize="small" />
                            {t("blogNavbar.search")}
                        </button>
                    </div>
                </div>

                {/* Right scroll button for mobile */}
                <IconButton 
                    className={`${classes.scrollButton} ${classes.scrollRight}`}
                    onClick={() => scroll('right')}
                    size="small"
                >
                    <ChevronRight />
                </IconButton>
            </div>

            {/* Search Dialog */}
            <Dialog 
                open={searchOpen} 
                onClose={handleSearchClose} 
                className={classes.searchDialog}
            >
                <DialogTitle>{t("search.dialog.title")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label={t("search.dialog.label")}
                        placeholder={t("search.dialog.placeholder")}
                        fullWidth
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className={classes.searchField}
                    />
                    
                    <Box className={classes.examplesContainer}>
                        <Info className={classes.infoIcon} />
                        <Typography variant="body2">
                             {t("search.dialog.examples")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleSearchClose} 
                        style={{ color: '#4A4A4A' }}
                    >
                        {t("search.dialog.cancel")}
                    </Button>
                    <Button 
                        onClick={handleSearchSubmit} 
                        style={{ 
                            backgroundColor: '#890608', 
                            color: '#ffffff' 
                        }}
                        variant="contained"
                        className={classes.searchButtonDialog}
                        disabled={!searchTerm.trim()}
                    >
                        {t("search.dialog.search")}
                    </Button>
                </DialogActions>
            </Dialog>
        </nav>
    );
};

export default NavbarBlog;